import { type CoreEventBus } from '@databases/core-event-bus';
import type { FlagsManager } from '@databases/flags-types';
import type { HighlightsController } from '@databases/highlights';
import type { PanelManager } from '@databases/panel-types';
import type { DatabasePermissionsManager } from '@databases/permissions';
import type { PluginsChecker } from '@databases/plugins-types';
import type { DatabaseSdkContract } from '@databases/sdk-types';
import type {
  SelectionsActions,
  SelectionsApiContract,
  SelectionsReader,
} from '@databases/selections-api-types';
import { type IntlShape } from 'react-intl-next';

import { type PreviewPanelAFMBridge } from '@canvas/databases-bridge-api';

import { ServiceContainer } from './service-container';
import { type FactoryFunction } from './types';

export type ContainerServices = {
  DatabaseSdk: DatabaseSdkContract;
  EventBus: CoreEventBus;
  FlagsManager: FlagsManager;
  Intl: IntlShape;
  PanelManager: PanelManager;
  PermissionsManager: DatabasePermissionsManager;
  PluginsManager: PluginsChecker;
  PreviewPanelAFMBridge: PreviewPanelAFMBridge;
  SelectionsActions: SelectionsActions;
  SelectionsApi: SelectionsApiContract;
  SelectionsReader: SelectionsReader;
  TableHighlightsController: HighlightsController;
};

export type ContainerOverrideOptions = {
  [K in keyof Omit<
    ContainerServices,
    | 'DatabaseSdk'
    | 'EventBus'
    | 'PermissionsManager'
    | 'SelectionsActions'
    | 'SelectionsReader'
    | 'SelectionsApi'
    | 'TableHighlightsController'
  >]: FactoryFunction<ContainerServices, ContainerServices[K]>;
};

export const container = new ServiceContainer<ContainerServices>();

/**
 * Simple utility for resolving services from the container.
 */
export function getService<K extends keyof ContainerServices>(
  key: K
): ContainerServices[K] {
  return container.resolve(key);
}
